import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { GetPsUpdateListParams, PsUpdate } from '../../../../api/model';
import { useGetPsChapterList, useGetPsUpdateList } from '../../../../api/pss/pss';
import { useDefaultOptions } from '../../../../hooks/api';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import ErrorComponent from '../../../atoms/ErrorComponent';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import PageNavi from '../../../modules/PageNavi';
import { getFormatDateYYYYMMDD } from './PsListCommmon';
import { SortableTh } from './PsListForm';

export interface PsUpdateListTabHandler {
  refresh: () => Promise<void>;
}
/**
 * PS履歴一覧タブ
 */
const PsUpdateListTab = forwardRef<PsUpdateListTabHandler>((_props, ref) => {
  // 1ページに表示する行数:50行
  const N_DATA_PER_PAGE = 50;

  // 翻訳用定数
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  // 検索条件 章
  const defaultCharterId = sessionStorageUtils.get('Pss.ChapterId', '');
  const [selectedCharterId, setSelectedCharterId] = useState(defaultCharterId);
  // 検索条件 作成者
  const defaultAuthor = sessionStorageUtils.get('Pss.Author', '');
  const [selectedAuthor, setSelectedAuthor] = useState(defaultAuthor);
  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  /* PS章一覧取得 */
  const {
    data: psChapterListData,
    isLoading: psChapterListIsLoading,
    isError: psChapterListIsError,
    error: psChapterListError,
  } = useGetPsChapterList(options);
  const chapterPsIdNum =
    selectedCharterId === 'all' || selectedCharterId === '' ? undefined : Number(selectedCharterId);
  const createUserIdNum = selectedAuthor === 'all' || selectedAuthor === '' ? undefined : Number(selectedAuthor);
  const updateListParams: GetPsUpdateListParams = { chapter_ps_id: chapterPsIdNum, create_user_id: createUserIdNum };

  /* PS一覧取得 */
  const {
    data: psUpdateListData,
    refetch: psUpdateListRefetch,
    isLoading: psUpdateListIsLoading,
    isError: psUpdateListIsError,
    error: psUpdateListError,
  } = useGetPsUpdateList(updateListParams, options);
  const uniqueUsers = useRef<PsUpdate[]>();

  /** ページネーション */
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Pss.CurrentPage', 1));

  /** ソート */
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Pss.SortInfo', { key: ['release_at'], isDisc: true })
  );

  /** ソート処理 */
  const allPss = useMemo(
    () =>
      [...(psUpdateListData?.data.ps_updates ?? [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;

        // 配列にソートキーがセットされていない場合は何もしない
        if (sortInfo.key.length === 0) {
          return 0;
        }

        // 配列にソートキーが1つだけの場合
        if (sortInfo.key.length === 1) {
          return ((aAny[sortInfo.key[0]] ?? '') > (bAny[sortInfo.key[0]] ?? '') ? 1 : -1) * multiValue;
        }

        // 配列にソートキーが2つ以上の場合(2つとみなして並び替え)
        // （所属 + 作成者)はソートキーが2つある
        if ((aAny[sortInfo.key[0]] ?? '') !== (bAny[sortInfo.key[0]] ?? '')) {
          return ((aAny[sortInfo.key[0]] ?? '') > (bAny[sortInfo.key[0]] ?? '') ? 1 : -1) * multiValue;
        }
        return ((aAny[sortInfo.key[1]] ?? '') > (bAny[sortInfo.key[1]] ?? '') ? 1 : -1) * multiValue;
      }),
    [sortInfo, psUpdateListData]
  );

  /** ページネーションで一覧をフィルタリングする */
  const filteredPss = useMemo(
    () => allPss.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, allPss]
  );

  /** ソートキーを保存 */
  const changeSortKey = useCallback((key: string[]) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: JSON.stringify(pre.key) === JSON.stringify(key) && !pre.isDisc };
      sessionStorageUtils.set('Pss.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  /** ページネーションを保存 */
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Pss.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  useEffect(() => {
    // ページネーションを1に戻す
    if (psUpdateListData?.data.ps_updates != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [psUpdateListData?.data.ps_updates, psUpdateListRefetch]);

  // 親コンポーネントからの操作
  useImperativeHandle(ref, () => ({
    refresh: async () => {
      await psUpdateListRefetch();
    },
  }));

  /* 章一覧のデータ */
  // ローディング
  if (psChapterListIsLoading) {
    return <WaitComponent />;
  }

  // エラー時
  if (psChapterListIsError) {
    return <ErrorComponent error={psChapterListError} />;
  }

  /* PS履歴一覧タブのデータ */
  if (psUpdateListIsLoading) {
    return <WaitComponent />;
  }

  // 更新履歴一覧取得エラー時
  if (psUpdateListIsError) {
    return <ErrorComponent error={psUpdateListError} />;
  }

  // データがない場合
  if (
    psChapterListData == null ||
    psChapterListData.data == null ||
    psChapterListData.data.ps_chapters == null ||
    psUpdateListData == null ||
    psUpdateListData.data == null ||
    psUpdateListData.data.ps_updates == null
  ) {
    return <NoDataComponent />;
  }

  /* PS作成者一覧取得 */
  if (
    (selectedCharterId === 'all' ||
      selectedCharterId === undefined ||
      selectedCharterId === null ||
      selectedCharterId === '') &&
    (selectedAuthor === 'all' || selectedAuthor === undefined || selectedAuthor === null || selectedAuthor === '')
  ) {
    uniqueUsers.current = Array.from(
      new Map(psUpdateListData.data.ps_updates.map((user) => [user.create_user_id, user])).values()
    );
    sessionStorageUtils.set('Pss.CreateUser', uniqueUsers.current);
  } else {
    uniqueUsers.current = sessionStorageUtils.get('Pss.CreateUser', []);
  }

  return (
    <>
      {/* 検索条件 */}
      <div className="ps-search">
        <div>
          <label htmlFor="chapter">{t('章の絞り込み')}</label>
          <span className="dropdown chapter">
            <select
              onChange={(e) => {
                setSelectedCharterId(e.target.value);
                sessionStorageUtils.set('Pss.ChapterId', e.target.value);
              }}
              value={selectedCharterId}
            >
              <option value="all" key="all">
                {t('すべて')}
              </option>
              {psChapterListData.data.ps_chapters.map((x) => (
                <option value={x.chapter_ps_id} key={x.chapter_ps_id}>
                  {x.chapter_ps_no}
                </option>
              ))}
            </select>
          </span>
        </div>
        <div>
          <label htmlFor="maker">{t('作成者の絞り込み')}</label>
          <span className="dropdown maker">
            <select
              onChange={(e) => {
                setSelectedAuthor(e.target.value);
                sessionStorageUtils.set('Pss.Author', e.target.value);
              }}
              value={selectedAuthor}
            >
              <option value="all" key="all">
                {t('すべて')}
              </option>
              {uniqueUsers.current !== undefined
                ? uniqueUsers.current.map((x) => (
                    <option value={x.create_user_id} key={x.create_user_id}>
                      {x.create_user_membership_name} {x.create_user}
                    </option>
                  ))
                : ''}
            </select>
          </span>
        </div>
      </div>
      <table className="table27">
        <tbody>
          <tr>
            {/* ラベル クリックで並び替え */}
            <SortableTh label={t('管理No.')} onClick={() => changeSortKey(['ps_version_id'])} />
            <SortableTh label={t('タイトル')} onClick={() => changeSortKey(['title'])} />
            <SortableTh
              label={t('作成者')}
              onClick={() => changeSortKey(['create_user_membership_name', 'create_user'])}
            />
            <SortableTh label={t('改訂理由')} onClick={() => changeSortKey(['release_note'])} />
            <SortableTh label={t('改訂日時')} onClick={() => changeSortKey(['release_at'])} />
          </tr>
          {/* データ表示部 */}
          {filteredPss.map((data) => (
            <tr key={data.ps_no?.toString()}>
              <td>
                <Link to={`/${language}/ps/view/${data.ps_version_id?.toString() ?? ''}`}> {data.ps_no}</Link>
              </td>
              <td>{data.title}</td>
              <td>
                {data.create_user_membership_name} {data.create_user}
              </td>
              <td>{data.release_note}</td>
              <td>{getFormatDateYYYYMMDD(data.release_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* ページング */}
      <PageNavi
        current={currentPage}
        pageCount={Math.ceil(psUpdateListData.data.ps_updates.length / (N_DATA_PER_PAGE * 1.0))}
        onChangePage={changeCurrentPage}
      />
    </>
  );
});

export default PsUpdateListTab;
